import { Close } from '@mui/icons-material';
import { Dialog, DialogActions, DialogContent, IconButton, Stack, Typography } from '@mui/material';
import React, { useId } from 'react';
import { Button } from 'new/elements/Button';
import { getDescriptionByStep } from 'new/modules/settings/capsules/utils/capsuleHelpers';
import { RegisterCapsuleStepsRow } from 'new/modules/settings/capsules/screens/CapsulesSettingsScreen/components/RegisterCapsuleModal/components/RegisterCapsuleStepsRow';
import {
  RegisterCapsuleModalProvider,
  useRegisterCapsule,
} from 'new/modules/settings/capsules/screens/CapsulesSettingsScreen/components/RegisterCapsuleModal/context/RegisterCapsuleModalContext';
import type { RegisterCapsuleDeployPayload } from 'new/modules/settings/capsules/screens/CapsulesSettingsScreen/components/RegisterCapsuleModal/context/RegisterCapsuleModalContext.types';
import { CopyKeysStep } from 'new/modules/settings/capsules/screens/CapsulesSettingsScreen/components/RegisterCapsuleModal/components/CopyKeysStep';
import { InfoStep } from 'new/modules/settings/capsules/screens/CapsulesSettingsScreen/components/RegisterCapsuleModal/components/InfoStep';
import { mapClusterCreateResponseToGeneratedKeys } from 'new/modules/settings/capsules/utils/mapClusterCreateResponseToGeneratedKeys';
import { validateRegisterCapsuleStepOne } from 'new/modules/settings/capsules/utils/registerCapsuleStepOneSchema';
import type { CapsuleDto } from 'new/stores/capsules/capsules.endpoints';

import styles from 'new/modules/settings/capsules/screens/CapsulesSettingsScreen/components/RegisterCapsuleModal/RegisterCapsuleModal.module.scss';

interface RegisterCapsuleModalProps {
  open: boolean;
  onClickRegisterKeys?: (
    deployPayload: RegisterCapsuleDeployPayload,
  ) => Promise<CapsuleDto | undefined>;
  onContinueToDeploy?: (createdClusterId: string | number | null) => void;
  onClose: () => void;
  isCreatingCluster?: boolean;
}

interface RegisterCapsuleModalContentProps {
  onClose: () => void;
  onClickRegisterKeys?: (
    deployPayload: RegisterCapsuleDeployPayload,
  ) => Promise<CapsuleDto | undefined>;
  onContinueToDeploy?: (createdClusterId: string | number | null) => void;
  isCreatingCluster?: boolean;
}

function RegisterCapsuleModalContent(props: RegisterCapsuleModalContentProps) {
  const { onClose, onClickRegisterKeys, onContinueToDeploy, isCreatingCluster } = props;
  const titleId = useId();
  const descriptionId = useId();
  const registerCapsuleWizard = useRegisterCapsule();
  const { state, actions, canContinueToDeploy, buildCreationClusterPayload } =
    registerCapsuleWizard;
  const { activeStep, capsuleName, capsuleDescription, createdClusterId } = state;
  const { applyKeysAndAdvanceToCopyStep, setCapsuleStepOneErrors } = actions;

  const validateStepOneOrSetErrors = (): boolean => {
    const stepOneValidation = validateRegisterCapsuleStepOne({
      capsuleName,
      capsuleDescription,
    });

    if (stepOneValidation.success === false) {
      setCapsuleStepOneErrors(
        stepOneValidation.errors.capsuleName ?? '',
        stepOneValidation.errors.capsuleDescription ?? '',
      );
      return false;
    }

    setCapsuleStepOneErrors('', '');
    return true;
  };

  const handleContinueToDeploy = () => {
    if (!canContinueToDeploy) return;
    onContinueToDeploy?.(createdClusterId);
    onClose();
  };

  const handleRegisterKeys = async () => {
    if (!validateStepOneOrSetErrors()) return;
    const payload = buildCreationClusterPayload();
    if (!payload) return;
    if (!onClickRegisterKeys) return;

    const dto = await onClickRegisterKeys(payload);
    if (!dto) return;

    const keys = mapClusterCreateResponseToGeneratedKeys(dto);
    applyKeysAndAdvanceToCopyStep(keys, dto.id, dto);
  };

  const generateKeysBusy = Boolean(isCreatingCluster);

  // The wizard shows one-time keys and the Helm command. Never dismiss on an
  // accidental backdrop click or Escape — closing must be deliberate (X or Done).
  const handleDialogClose = (_event: object, reason?: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    onClose();
  };

  return (
    <Dialog
      open
      onClose={handleDialogClose}
      disableEscapeKeyDown
      fullWidth
      maxWidth="lg"
      className={styles.dialog}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      PaperProps={{ className: styles.paper }}
    >
      <Stack className={styles.header}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          className={styles.headerTopRow}
        >
          <Typography id={titleId} variant="h6" className={styles.title}>
            Register a Capsule
          </Typography>
          <div className={styles.headerCenter}>
            <RegisterCapsuleStepsRow activeStep={activeStep} />
          </div>
          <div className={styles.headerRight}>
            <IconButton aria-label="Close modal" onClick={onClose} className={styles.closeButton}>
              <Close />
            </IconButton>
          </div>
        </Stack>
        <Typography id={descriptionId} variant="body2" className={styles.description}>
          {getDescriptionByStep(activeStep)}
        </Typography>
      </Stack>

      <DialogContent className={styles.body}>
        {activeStep === 1 ? <InfoStep /> : <CopyKeysStep />}
      </DialogContent>

      <DialogActions className={styles.footer}>
        <Stack direction="row" spacing={1.5} className={styles.footerActions}>
          {activeStep === 1 ? (
            <>
              <Button variant="outlined" onClick={onClose} disabled={generateKeysBusy}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  void handleRegisterKeys();
                }}
                disabled={generateKeysBusy}
              >
                {generateKeysBusy ? 'Generating…' : 'Generate Keys'}
              </Button>
            </>
          ) : null}

          {activeStep === 2 ? (
            <Button
              variant="contained"
              disabled={!canContinueToDeploy}
              onClick={handleContinueToDeploy}
            >
              Done
            </Button>
          ) : null}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

export function RegisterCapsuleModal(props: RegisterCapsuleModalProps) {
  const { open, onClose, onClickRegisterKeys, onContinueToDeploy, isCreatingCluster } = props;

  if (!open) return null;

  return (
    <RegisterCapsuleModalProvider>
      <RegisterCapsuleModalContent
        onClose={onClose}
        onClickRegisterKeys={onClickRegisterKeys}
        onContinueToDeploy={onContinueToDeploy}
        isCreatingCluster={isCreatingCluster}
      />
    </RegisterCapsuleModalProvider>
  );
}
